/**
 * The relationship chip row shared by the contact and organization QUICK-CREATE
 * dialogs.
 *
 * It exists for the same reason `RelationshipInlineEdit` does on the detail
 * pages: a tenant's own relationship classes ("Interior Designer") and the
 * built-in enum ("Customer") answer ONE question about a record, so a surface
 * that offers only the built-in half is asking half a question. The create
 * dialogs offered exactly that half — a tenant could define a class, see it on
 * the detail page and in the list filter, and then have no way to apply it at
 * the moment the record is born (WI 08ab4081).
 *
 * Two vocabularies, two STORED dimensions (an enum array on the row, a junction
 * beside it) and therefore two `onChange` callbacks — the create payload carries
 * both as separate fields. Only the control is one, divided by a line exactly as
 * the detail-page popover divides them.
 *
 * The class section renders only when the tenant HAS classes: a quick-create
 * dialog's budget is 3-5 fields (`.claude/rules/rules-ui-dialogs.md`), so it
 * cannot afford the discoverability empty state the detail-page picker carries —
 * that surface, and the admin page it links to, own teaching the feature.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import {
  resolveRelationshipClassLabel,
  selectableRelationshipClasses,
  useRelationshipClasses,
} from '../../../../shared/components/customFields';
import { ASSIGNABLE_RELATIONSHIP_VALUES } from '../utils/relationships';

/**
 * The i18n scope each entity labels the shared built-in vocabulary in — the same
 * map `RelationshipInlineEdit` keeps, because the entities disagree about a
 * value's wording (`recruitment` is "Recruitment Agency" for a company and
 * "Recruiter" for a contact).
 */
const RELATIONSHIP_LABEL_SCOPE = {
  contact: 'contactRelationship',
  organization: 'companyRelationship',
} as const;

interface RelationshipChipPickerProps {
  /** Which entity is being created — picks the label scope above. */
  entity: 'contact' | 'organization';
  /** The selected built-in relationship values. */
  value: string[];
  onChange: (next: string[]) => void;
  /** The selected tenant relationship-class ids (a separate stored dimension). */
  classValue: string[];
  onClassChange: (next: string[]) => void;
}

export function RelationshipChipPicker({
  entity,
  value,
  onChange,
  classValue,
  onClassChange,
}: RelationshipChipPickerProps) {
  const { t } = useTranslation(['contacts', 'common']);
  const { t: tcf, i18n } = useTranslation('custom_fields');
  const scope = RELATIONSHIP_LABEL_SCOPE[entity];

  // `includeInactive` so an archived class the selection already carries keeps an
  // option to render and un-tick (`.claude/rules/rules-frontend.md` § "A picker
  // that can REMOVE must fetch the retired options too"). A create starts with an
  // empty selection, so today this is the active list — it stays correct if the
  // control is ever handed a stored value.
  const { data: relationshipClasses } = useRelationshipClasses({ includeInactive: true });
  const classOptions = useMemo(
    () =>
      selectableRelationshipClasses(relationshipClasses, classValue).map((cls) => ({
        value: cls.id,
        label: resolveRelationshipClassLabel(cls, i18n.language),
      })),
    [relationshipClasses, classValue, i18n.language],
  );

  const toggle = (values: string[], toggled: string) =>
    values.includes(toggled)
      ? values.filter((v) => v !== toggled)
      : [...values, toggled];

  return (
    <div className="space-y-2">
      <Label>{t(`common:businessClassification.${scope}.label`)}</Label>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {ASSIGNABLE_RELATIONSHIP_VALUES.map((relationship) => {
            const isSelected = value.includes(relationship);
            return (
              <Button
                key={relationship}
                type="button"
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                onClick={() => onChange(toggle(value, relationship))}
                className="gap-1"
              >
                {isSelected && <Check className="h-3 w-3" />}
                {t(`common:businessClassification.${scope}.${relationship}`)}
              </Button>
            );
          })}
        </div>

        {classOptions.length > 0 && (
          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-xs font-medium text-muted-foreground">
              {tcf('relationshipClasses.fieldLabel')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {classOptions.map((option) => {
                const isSelected = classValue.includes(option.value);
                return (
                  <Button
                    key={option.value}
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onClassChange(toggle(classValue, option.value))}
                    className="gap-1"
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
